import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Inbox, FolderClosed, CheckCircle2, Download, ShieldCheck, Clock3 } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { downloadProtected } from "@/lib/files";
import api from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—");

export default function Despachos() {
  const [stats, setStats] = useState(null);
  const [firms, setFirms] = useState([]);
  const [loadingFirms, setLoadingFirms] = useState(true);
  const [downloading, setDownloading] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/firms")
      .then(({ data }) => setFirms(data))
      .catch(() => toast.error("No se pudo cargar el listado de despachos."))
      .finally(() => setLoadingFirms(false));
  }, []);

  const downloadAgreement = async (firm) => {
    setDownloading(firm.id);
    try {
      await downloadProtected(`/admin/firms/${firm.id}/agreement/copy`, `Acuerdo_${firm.name || "despacho"}.pdf`);
    } catch {
      toast.error("No se pudo descargar el justificante.");
    } finally {
      setDownloading(null);
    }
  };
  const s = stats || {};
  const cards = [
    { label: "Despachos en el piloto", value: s.firms, icon: Users },
    { label: "Solicitudes pendientes", value: s.pending_requests, icon: Inbox, to: "/admin/solicitudes" },
    { label: "En análisis", value: s.en_analisis, icon: FolderClosed, to: "/admin/asuntos" },
    { label: "Terminados", value: s.terminado, icon: CheckCircle2, to: "/admin/asuntos" },
  ];
  return (
    <AppLayout variant="admin">
      <div className="ddp-fade">
        <h1 className="ddp-serif" style={{ fontSize: "clamp(28px,4vw,40px)", color: "var(--burgundy-dark)", margin: 0, letterSpacing: "-.02em" }}>Despachos</h1>
        <p style={{ color: "var(--muted)", fontSize: 15, marginTop: 8 }}>Resumen operativo del piloto DDP.</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginTop: 28 }}>
        {cards.map((c) => {
          const inner = (
            <div className="ddp-card" style={{ padding: 24, height: "100%" }}>
              <c.icon size={20} style={{ color: "var(--burgundy)", marginBottom: 14 }} />
              <div className="ddp-serif" style={{ fontSize: 40, color: "var(--burgundy-dark)", lineHeight: 1 }}>{c.value ?? "—"}</div>
              <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 8, fontWeight: 600 }}>{c.label}</div>
            </div>
          );
          return c.to ? <Link key={c.label} to={c.to} style={{ textDecoration: "none" }}>{inner}</Link> : <div key={c.label}>{inner}</div>;
        })}
      </div>

      <section style={{ marginTop: 34 }}>
        <div style={{ marginBottom: 14 }}>
          <h2 className="ddp-serif" style={{ color: "var(--burgundy-dark)", fontSize: 25, margin: 0 }}>Formalización por despacho</h2>
          <p style={{ color: "var(--muted)", fontSize: 14, margin: "6px 0 0" }}>Estado del acuerdo, con independencia de que el despacho haya creado asuntos.</p>
        </div>

        {loadingFirms ? (
          <div className="ddp-card" style={{ padding: 24, color: "var(--muted)" }}>Cargando despachos…</div>
        ) : firms.length === 0 ? (
          <div className="ddp-card" style={{ padding: 24, color: "var(--muted)" }}>Todavía no hay despachos admitidos.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }} data-testid="admin-firms-list">
            {firms.map((firm) => {
              const agreement = firm.agreement || {};
              return (
                <div key={firm.id} className="ddp-card" style={{ padding: "18px 20px", display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }} data-testid={`admin-firm-${firm.id}`}>
                  <div style={{ flex: "1 1 250px", minWidth: 0 }}>
                    <div style={{ fontWeight: 750, color: "var(--ink)", fontSize: 15.5 }}>{firm.name}</div>
                    <div style={{ color: "var(--muted)", fontSize: 13.5, marginTop: 4, overflowWrap: "anywhere" }}>{firm.email || "Sin correo disponible"}</div>
                  </div>

                  <div style={{ flex: "1 1 270px", minWidth: 0 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: agreement.accepted ? "#35684A" : "#87641F", fontSize: 13.5, fontWeight: 700 }}>
                      {agreement.accepted ? <ShieldCheck size={16} /> : <Clock3 size={16} />}
                      {agreement.accepted ? "Acuerdo aceptado" : "Formalización pendiente"}
                    </div>
                    {agreement.accepted && (
                      <div style={{ color: "var(--muted)", fontSize: 12.5, marginTop: 5, lineHeight: 1.5 }}>
                        {fmtDate(agreement.accepted_at)} · v{agreement.version}<br />
                        <span style={{ overflowWrap: "anywhere" }}>{agreement.electronic_ref}</span>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="ddp-btn ddp-btn-ghost ddp-btn-sm"
                    disabled={!agreement.accepted || downloading === firm.id}
                    onClick={() => downloadAgreement(firm)}
                    data-testid={`admin-agreement-download-${firm.id}`}
                    title={agreement.accepted ? "Descargar justificante PDF" : "El acuerdo aún no se ha formalizado"}
                  >
                    <Download size={15} /> {downloading === firm.id ? "Descargando…" : "Descargar justificante"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
